import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { api, runAiJob, formatAxiosError } from "@/lib/api";
import { nextTypeAfterEnter, cycleType } from "./ElementLine";
import { uid, computeSceneNumbers, computeScenesIndex, computeCursorContext, buildContextPrefix, countWords } from "./helpers";
import ScreenplayToolbar from "./ScreenplayToolbar";
import SpellcheckModal from "@/components/SpellcheckModal";
import RenameCharacterModal from "@/components/RenameCharacterModal";
import ScreenplayVersionsButton from "@/components/ScreenplayVersionsButton";
import EditorPane from "./EditorPane";
import NavigatorPanel from "./NavigatorPanel";
import CharactersPanel from "./CharactersPanel";
import AssistantPanel from "./AssistantPanel";
import ScenesPanel from "./ScenesPanel";
import NotesPanel from "./NotesPanel";
import EscListener from "./EscListener";

/**
 * Top-level orchestrator of the Screenplay editor.
 *  - Loads screenplay from the backend, persists with 800ms debounce.
 *  - Owns ALL state (elements, characters, selection, cursor, AI preview).
 *  - Delegates rendering to focused sub-components.
 */
export default function ScreenplayTab({ projectId, project }) {
  const [screenplay, setScreenplay] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [autoFocusId, setAutoFocusId] = useState(null);
  // Posición del cursor al hacer foco automático en el nuevo elemento:
  //  - "end" (por defecto): al final del texto del elemento (caso clásico de
  //     Enter al final de la línea o llegada por flecha desde arriba/abajo).
  //  - "start": al principio (caso de split en medio de párrafo → la "cola"
  //     del texto va al nuevo elemento y queremos que el cursor quede ahí).
  const [autoFocusCursor, setAutoFocusCursor] = useState("end");
  const [cursorId, setCursorId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [sidePanel, setSidePanel] = useState("characters");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState("");
  const [aiPreview, setAiPreview] = useState(null);
  const [aiContext, setAiContext] = useState("");
  const [aiElapsed, setAiElapsed] = useState(0);
  const [directions, setDirections] = useState(null);
  const [typePickerForId, setTypePickerForId] = useState(null);
  // Modales de la barra de herramientas: 🔤 Ortografía y 🔄 Renombrar
  const [spellcheckOpen, setSpellcheckOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  // Cuando el usuario abre el panel de IA desde "Reescribir con IA" en la barra
  // de selección, forzamos el modo "rewrite" del AssistantPanel mediante un
  // nonce (cada click incrementa). Si abre el panel manualmente, el modo
  // permanece como lo dejó la última vez.
  const [assistantInitialMode, setAssistantInitialMode] = useState(null);
  const [assistantModeNonce, setAssistantModeNonce] = useState(0);
  // Navegador (tabla de contenidos del guion) — colapsable y persistido en localStorage.
  const [navCollapsed, setNavCollapsed] = useState(() => {
    try { return localStorage.getItem("screenplay:nav-collapsed") === "1"; } catch (_e) { return false; }
  });

  const debounceRef = useRef(null);
  // Guarda la última acción de IA para poder reintentarla desde el panel.
  const lastAiActionRef = useRef(null);
  // AbortController activo para que el botón Cancelar pueda detener el polling.
  const aiAbortRef = useRef(null);

  // ---------- UNDO/REDO ----------
  // Pila de snapshots para deshacer operaciones DESTRUCTIVAS a nivel de bloque
  // (cortar, borrar selección, pegar, limpiar huecos, aplicar IA, importar
  // FDX). El undo nativo del navegador solo funciona dentro de un <textarea>
  // — no recupera bloques borrados ni cambios entre bloques. Esto sí.
  //   - Máx. 30 snapshots para no inflar memoria.
  //   - `lastActionWasBlockRef` distingue: si la última acción fue de bloques,
  //     Cmd+Z lo deshace; si fue tipeo dentro de una textarea, dejamos
  //     pasar el undo NATIVO del navegador (más útil para revertir letras).
  const undoStackRef = useRef([]);
  const redoStackRef = useRef([]);
  const lastActionWasBlockRef = useRef(false);
  const lastSelectedRef = useRef(null);
  const [undoCount, setUndoCount] = useState(0); // re-render trigger para
  const [redoCount, setRedoCount] = useState(0); // habilitar/deshabilitar botones
  const [clipboardFeedback, setClipboardFeedback] = useState("");
  const flashFeedback = useCallback((msg) => {
    setClipboardFeedback(msg);
    setTimeout(() => setClipboardFeedback(""), 2200);
  }, []);

  // ---------- LOAD ----------
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get(`/icaa/projects/${projectId}/screenplay`)
      .then((r) => {
        if (cancelled) return;
        const sp = r.data || {};
        if (!sp.elements || sp.elements.length === 0) {
          sp.elements = [{ id: uid(), type: "scene_heading", text: "" }];
        }
        if (!sp.characters) sp.characters = [];
        if (sp.language == null) sp.language = "es";
        if (sp.title == null) sp.title = project?.title || "";
        setScreenplay(sp);
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error("Error loading screenplay", e);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [projectId, project?.title]);

  // ---------- DEBOUNCED SAVE ----------
  const persist = useCallback(
    async (sp) => {
      if (!sp) return;
      // 🛡 Cancela el guardado debounced pendiente — vamos a guardar AHORA
      // con un estado más reciente. Sin esto, un timer viejo (de tipeo
      // anterior) dispara 800ms más tarde y SOBRESCRIBE el estado actual
      // con datos obsoletos (causa: secuencias que reaparecen tras cortar,
      // textos a medias tras pegar/undo). Bug crítico antes de Feb 2026.
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      setSaving(true);
      try {
        await api.put(`/icaa/projects/${projectId}/screenplay`, {
          language: sp.language,
          title: sp.title,
          elements: sp.elements,
          characters: sp.characters,
          notes: sp.notes,
        });
        setSavedAt(new Date());
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("Save failed", e);
      } finally {
        setSaving(false);
      }
    },
    [projectId]
  );

  const scheduleSave = useCallback(
    (sp) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => persist(sp), 800);
    },
    [persist]
  );

  const updateScreenplay = useCallback(
    (mutator) => {
      setScreenplay((prev) => {
        if (!prev) return prev;
        const next = typeof mutator === "function" ? mutator(prev) : { ...prev, ...mutator };
        scheduleSave(next);
        return next;
      });
    },
    [scheduleSave]
  );

  // Force-save before unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        if (screenplay) persist(screenplay);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- ELEMENT HANDLERS ----------
  // Helper undo/redo: hace snapshot del array de elementos ANTES de una
  // operación destructiva. Llamar al inicio del op (no después).
  const pushUndo = useCallback(
    (opName) => {
      if (!screenplay) return;
      const snap = JSON.parse(JSON.stringify(screenplay.elements || []));
      undoStackRef.current.push({ snap, opName });
      if (undoStackRef.current.length > 30) undoStackRef.current.shift();
      redoStackRef.current = [];
      lastActionWasBlockRef.current = true;
      setUndoCount(undoStackRef.current.length);
      setRedoCount(0);
    },
    [screenplay]
  );

  const performUndo = useCallback(() => {
    if (undoStackRef.current.length === 0 || !screenplay) return false;
    const entry = undoStackRef.current.pop();
    // Guardar estado actual en redo antes de revertir
    const cur = JSON.parse(JSON.stringify(screenplay.elements || []));
    redoStackRef.current.push({ snap: cur, opName: entry.opName });
    if (redoStackRef.current.length > 30) redoStackRef.current.shift();
    const next = { ...screenplay, elements: entry.snap };
    setScreenplay(next);
    setSelectedIds(new Set());
    lastSelectedRef.current = null;
    persist(next);
    setUndoCount(undoStackRef.current.length);
    setRedoCount(redoStackRef.current.length);
    lastActionWasBlockRef.current = true; // siguiente Cmd+Z sigue en bloques
    setClipboardFeedback(`↶ Deshecho: ${entry.opName}`);
    setTimeout(() => setClipboardFeedback(""), 2200);
    return true;
  }, [screenplay]);

  const performRedo = useCallback(() => {
    if (redoStackRef.current.length === 0 || !screenplay) return false;
    const entry = redoStackRef.current.pop();
    const cur = JSON.parse(JSON.stringify(screenplay.elements || []));
    undoStackRef.current.push({ snap: cur, opName: entry.opName });
    if (undoStackRef.current.length > 30) undoStackRef.current.shift();
    const next = { ...screenplay, elements: entry.snap };
    setScreenplay(next);
    setSelectedIds(new Set());
    lastSelectedRef.current = null;
    persist(next);
    setUndoCount(undoStackRef.current.length);
    setRedoCount(redoStackRef.current.length);
    lastActionWasBlockRef.current = true;
    setClipboardFeedback(`↷ Rehecho: ${entry.opName}`);
    setTimeout(() => setClipboardFeedback(""), 2200);
    return true;
  }, [screenplay]);

  const updateElement = (id, patch) => {
    // Filtrar claves internas (prefijo "_") que no forman parte del modelo
    // de bloque y no deben persistirse en BD (ej. `_cursorPos`).
    const cleanPatch = {};
    for (const k of Object.keys(patch || {})) {
      if (!k.startsWith("_")) cleanPatch[k] = patch[k];
    }
    // Si el cambio es texto (tipeo) → marcamos que la última acción NO fue
    // de bloque, para que Cmd+Z respete el undo nativo del navegador.
    if ("text" in cleanPatch) {
      lastActionWasBlockRef.current = false;
    }
    updateScreenplay((sp) => ({
      ...sp,
      elements: sp.elements.map((el) => (el.id === id ? { ...el, ...cleanPatch } : el)),
    }));
  };

  const insertAfter = (id, type = "action", text = "", { cursor = "end" } = {}) => {
    const newEl = { id: uid(), type, text };
    updateScreenplay((sp) => {
      const idx = sp.elements.findIndex((el) => el.id === id);
      const next = [...sp.elements];
      next.splice(idx + 1, 0, newEl);
      return { ...sp, elements: next };
    });
    setAutoFocusCursor(cursor);
    setAutoFocusId(newEl.id);
    return newEl.id;
  };

  const deleteElement = (id) => {
    updateScreenplay((sp) => {
      if (sp.elements.length <= 1) {
        return { ...sp, elements: [{ id: uid(), type: "scene_heading", text: "" }] };
      }
      const idx = sp.elements.findIndex((el) => el.id === id);
      const next = sp.elements.filter((el) => el.id !== id);
      const focusIdx = Math.max(0, idx - 1);
      if (next[focusIdx]) setAutoFocusId(next[focusIdx].id);
      return { ...sp, elements: next };
    });
    setSelectedIds((s) => {
      const n = new Set(s);
      n.delete(id);
      return n;
    });
  };

  const handleEnter = (id, cursorPos) => {
    const cur = screenplay.elements.find((e) => e.id === id);
    if (!cur) return;
    const text = cur.text || "";
    // Split en la posición del cursor: la "cola" se mueve al nuevo elemento.
    // Si el cursor está en el final, la cola es "" → comportamiento anterior intacto.
    const pos = Number.isInteger(cursorPos) ? Math.max(0, Math.min(cursorPos, text.length)) : text.length;
    const head = text.slice(0, pos);
    const tail = text.slice(pos);
    const nextType = nextTypeAfterEnter(cur.type);
    if (tail.length > 0) {
      // Hay texto después del cursor → actualizamos el elemento actual y movemos
      // la cola al nuevo, con cursor al PRINCIPIO del nuevo (donde estaba antes
      // de pulsar Enter).
      updateElement(id, { text: head });
      insertAfter(id, nextType, tail, { cursor: "start" });
    } else {
      insertAfter(id, nextType, "", { cursor: "end" });
    }
  };

  const handleTab = (id, dir) => {
    const cur = screenplay.elements.find((e) => e.id === id);
    if (!cur) return;
    updateElement(id, { type: cycleType(cur.type, dir) });
  };

  const focusByIndex = (idx) => {
    if (!screenplay) return;
    if (idx < 0 || idx >= screenplay.elements.length) return;
    setAutoFocusId(screenplay.elements[idx].id);
  };

  const toggleSelected = (id, shiftKey = false) => {
    setSelectedIds((s) => {
      const n = new Set(s);
      // Shift+Click → seleccionar rango entre el último click y este (estilo
      // explorador de archivos). Funciona para selecciones masivas rápidas.
      if (shiftKey && lastSelectedRef.current && screenplay) {
        const ids = (screenplay.elements || []).map((e) => e.id);
        const i1 = ids.indexOf(lastSelectedRef.current);
        const i2 = ids.indexOf(id);
        if (i1 >= 0 && i2 >= 0) {
          const [a, b] = i1 < i2 ? [i1, i2] : [i2, i1];
          for (let i = a; i <= b; i++) n.add(ids[i]);
          return n;
        }
      }
      if (n.has(id)) n.delete(id); else n.add(id);
      lastSelectedRef.current = id;
      return n;
    });
  };

  // Drag-to-select entre bloques: cuando el usuario arrastra el cursor por
  // encima de varios bloques (cruzando textareas), pintamos como
  // seleccionado el rango completo desde `anchorId` hasta `currentId`. Esto
  // resuelve la limitación nativa de que <textarea> no permite selección
  // continua entre elementos. Reemplaza (no añade) la selección para que
  // el drag se sienta como una selección nueva y no acumule con la previa.
  const setDragSelectionRange = useCallback(
    (anchorId, currentId) => {
      if (!screenplay || !anchorId || !currentId) return;
      const ids = (screenplay.elements || []).map((e) => e.id);
      const i1 = ids.indexOf(anchorId);
      const i2 = ids.indexOf(currentId);
      if (i1 < 0 || i2 < 0) return;
      const [a, b] = i1 < i2 ? [i1, i2] : [i2, i1];
      const range = new Set();
      for (let i = a; i <= b; i++) range.add(ids[i]);
      setSelectedIds(range);
      lastSelectedRef.current = currentId;
    },
    [screenplay]
  );

  // Limpia bloques "fantasma" (sin texto tras trim) que se acumulan en el
  // guion y producen huecos visuales no borrables con Backspace. El export
  // PDF ya los filtra (screenplay_export.py l.105) — esto los elimina
  // también de la fuente de verdad. Protecciones:
  //   - Pide confirmación con el conteo.
  //   - Preserva el bloque con cursor activo (el usuario puede estar
  //     escribiendo justo ahí).
  //   - Si todo el guion quedara vacío, deja un bloque acción inicial.
  const cleanEmptyBlocks = () => {
    if (!screenplay) return;
    const isEmpty = (el) => !(el.text || "").trim();
    const protectedId = cursorId;
    const toRemove = (screenplay.elements || []).filter(
      (el) => isEmpty(el) && el.id !== protectedId
    );
    if (toRemove.length === 0) {
      window.alert("No hay bloques vacíos que limpiar. ✨");
      return;
    }
    const ok = window.confirm(
      `Se eliminarán ${toRemove.length} bloque(s) vacío(s) que crean huecos en el guion.\n\n¿Continuar?`
    );
    if (!ok) return;
    pushUndo(`Limpiar ${toRemove.length} hueco${toRemove.length === 1 ? "" : "s"}`);
    const removedIds = new Set(toRemove.map((e) => e.id));
    let cleaned = screenplay.elements.filter((el) => !removedIds.has(el.id));
    if (cleaned.length === 0) {
      cleaned = [{ id: uid(), type: "action", text: "" }];
    }
    const next = { ...screenplay, elements: cleaned };
    setScreenplay(next);
    persist(next);
    // Limpiar selección por si algún borrado estaba seleccionado.
    if (selectedIds.size > 0) {
      const stillExisting = new Set(
        [...selectedIds].filter((id) => !removedIds.has(id))
      );
      setSelectedIds(stillExisting);
    }
  };

  // Borrar todos los bloques seleccionados de golpe — útil para limpiar zonas
  // grandes. Pide confirmación si son >2 para evitar accidentes.
  const deleteSelected = async () => {
    if (selectedIds.size === 0 || !screenplay) return;
    if (selectedIds.size >= 3) {
      const ok = window.confirm(`¿Borrar ${selectedIds.size} bloques seleccionados?`);
      if (!ok) return;
    }
    pushUndo(`Borrar ${selectedIds.size} bloque${selectedIds.size === 1 ? "" : "s"}`);
    const remaining = screenplay.elements.filter((e) => !selectedIds.has(e.id));
    // Garantizar que siempre quede al menos 1 bloque vacío
    const finalElements = remaining.length > 0
      ? remaining
      : [{ id: crypto.randomUUID(), type: "action", text: "" }];
    setScreenplay({ ...screenplay, elements: finalElements });
    setSelectedIds(new Set());
    lastSelectedRef.current = null;
    persist({ ...screenplay, elements: finalElements });
  };

  // ---------- COPY / CUT / PASTE BLOQUES ----------
  // Permite manipular MÚLTIPLES bloques completos (acción + personaje +
  // diálogo + etc.) como un módulo. La selección nativa del navegador NO
  // puede saltar entre <textarea>, así que ofrecemos atajos propios sobre
  // los `selectedIds` que ya existen (Shift+Click).
  //
  // Atajos:
  //   ⌘/Ctrl + C → copia los bloques seleccionados (preserva tipo)
  //   ⌘/Ctrl + X → corta (copia + borra)
  //   ⌘/Ctrl + V → pega los bloques DESPUÉS del último seleccionado o de
  //                la línea con foco
  //
  // Persistencia entre sesiones de pestaña: sessionStorage. Entre pestañas
  // distintas también funciona porque la misma URL comparte sessionStorage
  // por ventana (aceptable para nuestro caso — el usuario suele trabajar
  // en una sola ventana del editor).

  // Helper para leer el clipboard interno desde sessionStorage. Devuelve la
  // lista de items o `null` si no hay nada serializado.
  const _readClipboard = () => {
    try {
      const raw = sessionStorage.getItem("gb_screenplay_clipboard");
      if (!raw) return null;
      const obj = JSON.parse(raw);
      if (obj?.kind !== "gb-screenplay-blocks") return null;
      return Array.isArray(obj.items) && obj.items.length > 0 ? obj.items : null;
    } catch (_e) {
      return null;
    }
  };

  const copySelected = useCallback(() => {
    if (selectedIds.size === 0 || !screenplay) return;
    // Preservar ORDEN del documento, no el orden de selección
    const items = screenplay.elements
      .filter((e) => selectedIds.has(e.id))
      .map((e) => ({ type: e.type, text: e.text || "" }));
    if (items.length === 0) return;
    try {
      sessionStorage.setItem(
        "gb_screenplay_clipboard",
        JSON.stringify({ kind: "gb-screenplay-blocks", items })
      );
    } catch (_e) { /* sessionStorage puede estar deshabilitado */ }
    // Además, escribimos texto plano al portapapeles del sistema para que
    // el usuario pueda pegar en Final Draft, Word, etc. si lo necesita.
    try {
      const plain = items.map((it) => it.text).join("\n");
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(plain).catch(() => { /* silent */ });
      }
    } catch (_e) { /* no clipboard permission */ }
    flashFeedback(`✓ ${items.length} bloque${items.length === 1 ? "" : "s"} copiado${items.length === 1 ? "" : "s"}`);
  }, [selectedIds, screenplay, flashFeedback]);

  const cutSelected = useCallback(() => {
    if (selectedIds.size === 0 || !screenplay) return;
    // Copia + borra. No usamos deleteSelected() directamente porque pide
    // confirmación con ≥3 bloques y queremos cortar sin fricción.
    const items = screenplay.elements
      .filter((e) => selectedIds.has(e.id))
      .map((e) => ({ type: e.type, text: e.text || "" }));
    if (items.length === 0) return;
    try {
      sessionStorage.setItem(
        "gb_screenplay_clipboard",
        JSON.stringify({ kind: "gb-screenplay-blocks", items })
      );
      const plain = items.map((it) => it.text).join("\n");
      if (navigator.clipboard?.writeText) navigator.clipboard.writeText(plain).catch(() => {});
    } catch (_e) { /* silent */ }
    pushUndo(`Cortar ${items.length} bloque${items.length === 1 ? "" : "s"}`);
    const remaining = screenplay.elements.filter((e) => !selectedIds.has(e.id));
    const finalElements = remaining.length > 0
      ? remaining
      : [{ id: uid(), type: "action", text: "" }];
    setScreenplay({ ...screenplay, elements: finalElements });
    setSelectedIds(new Set());
    lastSelectedRef.current = null;
    persist({ ...screenplay, elements: finalElements });
    flashFeedback(`✂ ${items.length} bloque${items.length === 1 ? "" : "s"} cortado${items.length === 1 ? "" : "s"}`);
  }, [selectedIds, screenplay, persist, flashFeedback]);

  const pasteBlocks = useCallback(() => {
    const items = _readClipboard();
    if (!items || !screenplay) return false;
    // Decidir punto de inserción:
    //  1. Si hay bloques seleccionados → después del último (en orden de doc)
    //  2. Si hay cursor/foco activo → después de ese
    //  3. Si no, al final
    const elements = screenplay.elements;
    let anchorIdx = -1;
    if (selectedIds.size > 0) {
      for (let i = elements.length - 1; i >= 0; i--) {
        if (selectedIds.has(elements[i].id)) { anchorIdx = i; break; }
      }
    } else if (cursorId) {
      anchorIdx = elements.findIndex((e) => e.id === cursorId);
    } else if (autoFocusId) {
      anchorIdx = elements.findIndex((e) => e.id === autoFocusId);
    }
    if (anchorIdx < 0) anchorIdx = elements.length - 1;
    pushUndo(`Pegar ${items.length} bloque${items.length === 1 ? "" : "s"}`);
    const newItems = items.map((it) => ({
      id: uid(),
      type: it.type || "action",
      text: it.text || "",
    }));
    const newElements = [
      ...elements.slice(0, anchorIdx + 1),
      ...newItems,
      ...elements.slice(anchorIdx + 1),
    ];
    setScreenplay({ ...screenplay, elements: newElements });
    setSelectedIds(new Set());
    // Foco al ÚLTIMO bloque pegado para flow natural
    setAutoFocusCursor("end");
    setAutoFocusId(newItems[newItems.length - 1].id);
    persist({ ...screenplay, elements: newElements });
    flashFeedback(`✓ ${items.length} bloque${items.length === 1 ? "" : "s"} pegado${items.length === 1 ? "" : "s"}`);
    return true;
  }, [screenplay, selectedIds, cursorId, autoFocusId, persist, flashFeedback]);

  const addSceneHeading = (preset) => {
    if (!screenplay) return;
    const elements = screenplay.elements;
    // Anchor at where the user is (cursor / last focused), or fall back to last line
    const anchorId = cursorId || autoFocusId || elements[elements.length - 1].id;
    const anchor = elements.find((e) => e.id === anchorId);
    // If the cursor sits on an EMPTY line, REPLACE it in-place (most common case:
    // user clicks the preset right after pressing Enter, or on a fresh project).
    if (anchor && !((anchor.text || "").trim())) {
      updateElement(anchorId, { type: "scene_heading", text: preset });
      setAutoFocusId(anchorId);
      return;
    }
    // Otherwise insert a new scene heading right after the anchor line.
    const id = insertAfter(anchorId, "scene_heading", preset);
    setTimeout(() => setAutoFocusId(id), 50);
  };

  // ---------- DERIVED ----------
  const sceneNumbers = useMemo(() => computeSceneNumbers(screenplay?.elements), [screenplay?.elements]);
  const scenesIndex = useMemo(() => computeScenesIndex(screenplay?.elements), [screenplay?.elements]);
  const cursorContext = useMemo(
    () => computeCursorContext(screenplay?.elements, cursorId, autoFocusId),
    [screenplay?.elements, cursorId, autoFocusId]
  );
  const stats = useMemo(() => ({
    lines: screenplay?.elements?.length || 0,
    scenes: scenesIndex.length,
    characters: (screenplay?.characters || []).length,
    words: countWords(screenplay?.elements),
  }), [screenplay, scenesIndex]);

  // ---------- NAVIGATOR ----------
  // Escena en la que está el cursor (busca hacia atrás hasta encontrar un scene_heading).
  const activeSceneNumber = useMemo(() => {
    const els = screenplay?.elements || [];
    const refId = cursorId || autoFocusId;
    if (!refId) return null;
    const idx = els.findIndex((e) => e.id === refId);
    if (idx < 0) return null;
    for (let i = idx; i >= 0; i--) {
      if (els[i].type === "scene_heading") return sceneNumbers[els[i].id] || null;
    }
    return null;
  }, [screenplay?.elements, sceneNumbers, cursorId, autoFocusId]);

  const scrollToElement = useCallback((elementId, { focus = false } = {}) => {
    if (!elementId) return;
    const node = document.getElementById(`sp-line-${elementId}`);
    if (!node) return;
    node.scrollIntoView({ behavior: "smooth", block: "start" });
    if (focus) setAutoFocusId(elementId);
  }, []);

  const jumpToScene = useCallback(
    (scene) => scrollToElement(scene?.elementId, { focus: true }),
    [scrollToElement]
  );

  const jumpToStart = useCallback(() => {
    const els = screenplay?.elements || [];
    if (els[0]) scrollToElement(els[0].id, { focus: true });
  }, [screenplay?.elements, scrollToElement]);

  const jumpToEnd = useCallback(() => {
    const els = screenplay?.elements || [];
    if (els.length) scrollToElement(els[els.length - 1].id, { focus: true });
  }, [screenplay?.elements, scrollToElement]);

  const jumpToAdjacentScene = useCallback(
    (delta) => {
      if (!scenesIndex.length) return;
      const curIdx = scenesIndex.findIndex((s) => s.number === activeSceneNumber);
      const baseIdx = curIdx < 0 ? (delta > 0 ? -1 : scenesIndex.length) : curIdx;
      const nextIdx = Math.max(0, Math.min(scenesIndex.length - 1, baseIdx + delta));
      jumpToScene(scenesIndex[nextIdx]);
    },
    [scenesIndex, activeSceneNumber, jumpToScene]
  );

  const toggleNavCollapsed = useCallback(() => {
    setNavCollapsed((v) => {
      const next = !v;
      try { localStorage.setItem("screenplay:nav-collapsed", next ? "1" : "0"); } catch (_e) { /* ignore */ }
      return next;
    });
  }, []);

  // Atajos globales: ⌘↑/↓ inicio/final, ⌘[/] escena anterior/siguiente,
  // ⌘/Ctrl+C/X/V copiar/cortar/pegar bloques seleccionados.
  useEffect(() => {
    const onKey = (e) => {
      if (!(e.metaKey || e.ctrlKey)) return;
      // ── Undo / Redo a nivel de BLOQUES ─────────────────────────────
      // ⌘/Ctrl + Z → deshacer (solo si la última acción fue de bloques;
      //   si no, dejamos pasar el undo NATIVO del navegador en la textarea).
      // ⌘/Ctrl + Shift + Z  o  ⌘/Ctrl + Y → rehacer.
      if ((e.key === "z" || e.key === "Z") && e.shiftKey && !e.altKey) {
        if (redoStackRef.current.length > 0) {
          e.preventDefault();
          performRedo();
        }
        return;
      }
      if ((e.key === "y" || e.key === "Y") && !e.shiftKey && !e.altKey) {
        if (redoStackRef.current.length > 0) {
          e.preventDefault();
          performRedo();
        }
        return;
      }
      if ((e.key === "z" || e.key === "Z") && !e.shiftKey && !e.altKey) {
        if (lastActionWasBlockRef.current && undoStackRef.current.length > 0) {
          e.preventDefault();
          performUndo();
        }
        // Si la última acción fue tipeo, dejamos pasar → undo nativo del navegador.
        return;
      }
      // Ignorar si el usuario está escribiendo en un input/textarea SIN modifier extra
      // (porque ⌘↑/↓ tradicionalmente saltan principio/final del documento, lo cual
      // queremos justamente). Pero NO interceptamos si está escribiendo + shift, para
      // no romper selecciones.
      if (e.shiftKey || e.altKey) return;
      // ── Copy / Cut / Paste de bloques seleccionados ────────────────
      // Solo interceptamos si hay bloques seleccionados (Shift+Click). Si
      // el usuario no tiene selección y pulsa ⌘+C dentro de un textarea,
      // el navegador hace su comportamiento estándar (copiar texto).
      const tgt = e.target;
      const inTextField = tgt && (tgt.tagName === "TEXTAREA" || tgt.tagName === "INPUT");
      if ((e.key === "c" || e.key === "C") && selectedIds.size > 0) {
        e.preventDefault();
        copySelected();
        return;
      }
      if ((e.key === "x" || e.key === "X") && selectedIds.size > 0) {
        e.preventDefault();
        cutSelected();
        return;
      }
      if (e.key === "v" || e.key === "V") {
        // Solo intercepta paste si:
        //  - hay clipboard interno con bloques
        //  - Y (no estamos escribiendo en un campo  O  hay bloques seleccionados)
        // Si está escribiendo en una textarea SIN selección de bloques, dejamos
        // el paste normal del navegador (pegado de texto).
        let hasInternalClipboard = false;
        try {
          const raw = sessionStorage.getItem("gb_screenplay_clipboard");
          if (raw) {
            const obj = JSON.parse(raw);
            hasInternalClipboard = obj?.kind === "gb-screenplay-blocks" && (obj.items || []).length > 0;
          }
        } catch (_e) { /* ignore */ }
        if (hasInternalClipboard && (!inTextField || selectedIds.size > 0)) {
          e.preventDefault();
          pasteBlocks();
          return;
        }
      }
      // ── Navegación ⌘↑/↓ /[]  ───────────────────────────────────────
      if (e.key === "ArrowUp") { e.preventDefault(); jumpToStart(); }
      else if (e.key === "ArrowDown") { e.preventDefault(); jumpToEnd(); }
      else if (e.key === "[") { e.preventDefault(); jumpToAdjacentScene(-1); }
      else if (e.key === "]") { e.preventDefault(); jumpToAdjacentScene(1); }
      // ⌘/Ctrl + Backspace o Delete → borrar bloques seleccionados (solo si los hay)
      else if ((e.key === "Backspace" || e.key === "Delete") && selectedIds.size > 0) {
        e.preventDefault();
        deleteSelected();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [jumpToStart, jumpToEnd, jumpToAdjacentScene, selectedIds, deleteSelected, copySelected, cutSelected, pasteBlocks, performUndo, performRedo]);

  // ---------- AI ACTIONS ----------
  // Helper común: lanza un job de IA con polling, sin límite total, con
  // posibilidad de cancelar y con contador de segundos transcurridos.
  async function runAi(action, params, { onSuccess, contextSnapshot }) {
    // Si hay otro job corriendo, cancelarlo antes (1 acción IA a la vez).
    if (aiAbortRef.current) {
      try { aiAbortRef.current.abort(); } catch (_e) { /* ignore */ }
    }
    const controller = new AbortController();
    aiAbortRef.current = controller;
    setAiBusy(true);
    setAiError("");
    setAiPreview(null);
    setAiElapsed(0);
    if (contextSnapshot !== undefined) setAiContext(contextSnapshot);
    try {
      const result = await runAiJob(projectId, action, params, {
        signal: controller.signal,
        onProgress: (job) => {
          setAiElapsed(job.elapsed_seconds || 0);
        },
      });
      onSuccess(result);
    } catch (e) {
      if (e.name === "AbortError") {
        // El usuario canceló → no mostramos error, simplemente limpiamos.
        setAiError("");
      } else {
        setAiError(formatAxiosError(e));
      }
    } finally {
      setAiBusy(false);
      aiAbortRef.current = null;
    }
  }

  async function aiGenerateSequence(instructions, targetChars, includeContext) {
    if (!instructions?.trim() && !includeContext) return;
    lastAiActionRef.current = () => aiGenerateSequence(instructions, targetChars, includeContext);
    let finalInstr = instructions || "";
    if (includeContext && cursorContext) {
      const prefix = buildContextPrefix(cursorContext);
      finalInstr = (prefix ? prefix + "\n\nIntención del autor: " : "") + (instructions || "Continúa la escena de forma orgánica.");
    }
    await runAi(
      "generate-sequence",
      {
        instructions: finalInstr,
        characters: targetChars && targetChars.length ? targetChars : null,
      },
      {
        contextSnapshot: finalInstr,
        onSuccess: (result) => {
          setAiPreview({ kind: "sequence", elements: result?.elements || [] });
        },
      }
    );
  }

  async function aiSuggestDirections(extraHint) {
    lastAiActionRef.current = () => aiSuggestDirections(extraHint);
    setDirections(null);
    await runAi(
      "suggest-directions",
      {
        anchor_element_id: cursorContext?.anchorId || null,
        extra_hint: (extraHint || "").trim(),
      },
      {
        onSuccess: (result) => {
          setDirections(result || { directions: [] });
        },
      }
    );
  }

  async function aiGenerateDialogue(sceneContext, speakers, extra) {
    if (!sceneContext?.trim()) return;
    lastAiActionRef.current = () => aiGenerateDialogue(sceneContext, speakers, extra);
    await runAi(
      "generate-dialogue",
      {
        scene_context: sceneContext,
        speakers: speakers || [],
        instructions: extra || "",
      },
      {
        contextSnapshot: sceneContext,
        onSuccess: (result) => {
          setAiPreview({ kind: "dialogue", elements: result?.elements || [] });
        },
      }
    );
  }

  async function aiRewriteSelection(instructions) {
    if (!instructions?.trim()) return;
    const sel = Array.from(selectedIds);
    if (sel.length === 0) {
      setAiError("Selecciona al menos una línea para reescribir.");
      return;
    }
    lastAiActionRef.current = () => aiRewriteSelection(instructions);
    const elements = screenplay.elements.filter((e) => selectedIds.has(e.id));
    await runAi(
      "rewrite",
      {
        elements: elements.map((el) => ({ type: el.type, text: el.text })),
        instructions,
      },
      {
        contextSnapshot: instructions,
        onSuccess: (result) => {
          setAiPreview({ kind: "rewrite", elements: result?.elements || [], selectedIds: sel });
        },
      }
    );
  }

  const retryLastAiAction = () => {
    const last = lastAiActionRef.current;
    if (!last || aiBusy) return;
    setAiError("");
    last();
  };

  const cancelAi = () => {
    if (aiAbortRef.current) {
      try { aiAbortRef.current.abort(); } catch (_e) { /* ignore */ }
    }
  };

  // Limpieza al desmontar: aborta cualquier job en curso.
  useEffect(() => {
    return () => {
      if (aiAbortRef.current) {
        try { aiAbortRef.current.abort(); } catch (_e) { /* ignore */ }
      }
    };
  }, []);

  const applyAiPreview = (mode) => {
    if (!aiPreview) return;
    const newEls = (aiPreview.elements || []).map((el) => ({
      id: uid(),
      type: el.type,
      text: el.text || "",
    }));
    if (newEls.length === 0) {
      setAiError("La IA no devolvió contenido. Inténtalo de nuevo.");
      return;
    }
    if (aiPreview.kind === "rewrite") {
      pushUndo(`Reescribir ${newEls.length} bloque${newEls.length === 1 ? "" : "s"} con IA`);
      updateScreenplay((sp) => {
        const selSet = new Set(aiPreview.selectedIds || Array.from(selectedIds));
        const elements = [];
        let inserted = false;
        for (const el of sp.elements) {
          if (selSet.has(el.id)) {
            if (!inserted) { elements.push(...newEls); inserted = true; }
          } else {
            elements.push(el);
          }
        }
        if (!inserted) elements.push(...newEls);
        return { ...sp, elements };
      });
      setSelectedIds(new Set());
    } else {
      pushUndo(`Insertar ${newEls.length} bloque${newEls.length === 1 ? "" : "s"} de IA`);
      updateScreenplay((sp) => {
        let insertIdx = sp.elements.length;
        if (mode === "after-focus" && autoFocusId) {
          insertIdx = sp.elements.findIndex((e) => e.id === autoFocusId) + 1;
        }
        const elements = [...sp.elements];
        elements.splice(insertIdx, 0, ...newEls);
        return { ...sp, elements };
      });
    }
    setAiPreview(null);
  };

  // ---------- IMPORT ----------
  const handleImportFdx = async (file) => {
    if (!confirm(`Vas a reemplazar el guion actual con el contenido de ${file.name}. ¿Continuar?`)) return;
    pushUndo(`Importar ${file.name}`);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/icaa/projects/${projectId}/screenplay/import/fdx`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const r2 = await api.get(`/icaa/projects/${projectId}/screenplay`);
      const sp = r2.data || {};
      if (!sp.elements || sp.elements.length === 0) {
        sp.elements = [{ id: uid(), type: "scene_heading", text: "" }];
      }
      setScreenplay(sp);
      alert(`Importadas ${r.data?.elements_count || 0} líneas desde Final Draft.`);
    } catch (err) {
      alert("Error al importar: " + (err.response?.data?.detail || err.message));
    }
  };

  // ---------- CHARACTERS ----------
  // IMPORTANT: per-keystroke PUTs to /characters/{cid} caused a race condition where
  // late responses overwrote the local state while the user was still typing
  // (typing "Mi motivación…" yielded "Mimtvci…"). Now updates are optimistic-local
  // and persistence rides on the debounced /screenplay PUT (which accepts characters).
  const charPanelProps = {
    projectId,
    characters: screenplay?.characters || [],
    onRefresh: async () => {
      // Llamado tras refinar nombres: el backend ha sustituido en el guion
      // y unificado fichas, recargamos todo el screenplay.
      try {
        const r = await api.get(`/icaa/projects/${projectId}/screenplay`);
        setScreenplay(r.data);
      } catch (e) {
        console.error("Failed to reload screenplay after refine:", e);
      }
    },
    onAdd: async (data) => {
      try {
        const r = await api.post(`/icaa/projects/${projectId}/screenplay/characters`, data);
        updateScreenplay((sp) => ({ ...sp, characters: [...(sp.characters || []), r.data] }));
      } catch (e) { alert("Error al crear personaje: " + (e.response?.data?.detail || e.message)); }
    },
    onUpdate: (cid, patch) => {
      // Optimistic local update + debounced screenplay save (no per-keystroke API call).
      updateScreenplay((sp) => ({
        ...sp,
        characters: (sp.characters || []).map((c) => (c.id === cid ? { ...c, ...patch } : c)),
      }));
    },
    onDelete: async (cid) => {
      try {
        await api.delete(`/icaa/projects/${projectId}/screenplay/characters/${cid}`);
        updateScreenplay((sp) => ({ ...sp, characters: (sp.characters || []).filter((c) => c.id !== cid) }));
      } catch (e) { alert("Error al borrar: " + (e.response?.data?.detail || e.message)); }
    },
  };

  // ---------- RENDER ----------
  if (loading || !screenplay) {
    return (
      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-8" data-testid="screenplay-loading">
        <p className="text-center text-stone-500">Cargando guion…</p>
      </section>
    );
  }

  const Wrapper = fullscreen ? "div" : "section";
  const wrapperClass = fullscreen
    ? "fixed inset-0 z-50 flex flex-col bg-[#FAFAF6]"
    : "mx-auto w-full max-w-[1600px] px-2 py-4 sm:px-6";

  const charNames = (screenplay.characters || []).map((c) => c.name).filter(Boolean);

  const sideTabs = [
    { id: "characters", label: "Personajes", count: (screenplay.characters || []).length },
    { id: "assistant", label: "Asistente IA" },
    { id: "scenes", label: "Escenas", count: scenesIndex.length },
    { id: "notes", label: "Notas" },
  ];

  return (
    <Wrapper className={wrapperClass} data-testid="screenplay-tab">
      <ScreenplayVersionsButton
        projectId={projectId}
        currentScreenplay={screenplay}
        stats={stats}
        onRestore={(newSp) => {
          // Restaura el snapshot al guion activo: actualiza state + persiste.
          if (debounceRef.current) clearTimeout(debounceRef.current);
          setScreenplay(newSp);
          persist(newSp);
        }}
      />
      <ScreenplayToolbar
        projectId={projectId}
        title={screenplay.title}
        language={screenplay.language}
        saving={saving}
        savedAt={savedAt}
        stats={stats}
        fullscreen={fullscreen}
        onToggleFullscreen={() => setFullscreen((v) => !v)}
        onChangeTitle={(v) => updateScreenplay({ title: v })}
        onChangeLanguage={(v) => updateScreenplay({ language: v })}
        onImportFdx={handleImportFdx}
        onOpenSpellcheck={() => setSpellcheckOpen(true)}
        onOpenRename={() => setRenameOpen(true)}
        onCleanEmpty={cleanEmptyBlocks}
        onUndo={performUndo}
        onRedo={performRedo}
        canUndo={undoCount > 0}
        canRedo={redoCount > 0}
      />

      {/* Body: 3-pane layout (Navigator + Editor + Side panel) */}
      <div className={`flex min-h-0 flex-1 flex-col gap-3 p-3 lg:flex-row lg:gap-4 ${fullscreen ? "overflow-hidden" : ""}`}>
        <NavigatorPanel
          scenes={scenesIndex}
          activeSceneNumber={activeSceneNumber}
          totalWords={stats.words}
          collapsed={navCollapsed}
          onToggleCollapsed={toggleNavCollapsed}
          onJumpToScene={jumpToScene}
          onJumpToStart={jumpToStart}
          onJumpToEnd={jumpToEnd}
        />
        <EditorPane
          elements={screenplay.elements}
          language={screenplay.language}
          sceneNumbers={sceneNumbers}
          selectedIds={selectedIds}
          autoFocusId={autoFocusId}
          autoFocusCursor={autoFocusCursor}
          typePickerForId={typePickerForId}
          characterMatrix={(screenplay.characters || []).map((c) => c.name).filter(Boolean)}
          onUpdateElement={updateElement}
          onChangeType={(id, type) => updateElement(id, { type })}
          onEnter={handleEnter}
          onTab={handleTab}
          onBackspaceEmpty={deleteElement}
          onArrowUp={(idx) => focusByIndex(idx - 1)}
          onArrowDown={(idx) => focusByIndex(idx + 1)}
          onToggleSelect={toggleSelected}
          onDragSelectRange={setDragSelectionRange}
          onFocus={(id) => { setCursorId(id); setTypePickerForId(null); }}
          onRequestTypePicker={(id) => setTypePickerForId(id)}
          onPickType={(id, type) => {
            // Auto-prefijo para sobreimpresión: si la línea está vacía, arranca con
            // "SOBREIMPRESO: " para que el usuario solo escriba el rótulo.
            const cur = screenplay.elements.find((e) => e.id === id);
            const patch = { type };
            const empty = !(cur?.text || "").trim();
            if (type === "subtitle" && empty) {
              patch.text = "SOBREIMPRESO: ";
            }
            // Acotación: auto-paréntesis. Si la línea está vacía, ponemos "()"
            // y dejamos el cursor en posición 1 (entre los paréntesis).
            // Si ya tiene texto, lo envolvemos: "texto" → "(texto)".
            if (type === "parenthetical") {
              if (empty) {
                patch.text = "()";
                patch._cursorPos = 1;  // señal al ElementLine para posicionar entre paréntesis
              } else {
                const t = (cur.text || "").trim();
                if (!t.startsWith("(")) patch.text = `(${t}`;
                if (!t.endsWith(")")) patch.text = (patch.text || cur.text) + ")";
              }
            }
            updateElement(id, patch);
            setTypePickerForId(null);
            setAutoFocusId(id);
            // Si hay _cursorPos, mandamos también el cursor a esa posición.
            if (patch._cursorPos != null) {
              setAutoFocusCursor("paren-inside");
            }
          }}
          onCancelTypePicker={() => setTypePickerForId(null)}
          onInsertAfter={insertAfter}
          onAddPreset={addSceneHeading}
          onOpenRewrite={() => {
            setSidePanel("assistant");
            setAssistantInitialMode("rewrite");
            setAssistantModeNonce((n) => n + 1);
          }}
          onDeleteSelected={deleteSelected}
          onCopySelected={copySelected}
          onCutSelected={cutSelected}
          onClearSelection={() => { setSelectedIds(new Set()); lastSelectedRef.current = null; }}
          selectedCount={selectedIds.size}
        />

        {/* Side panel — sticks to the top while user scrolls long screenplays in non-fullscreen.
            In fullscreen the surrounding container has overflow-hidden so it stays naturally. */}
        <div
          className={`flex w-full shrink-0 flex-col overflow-hidden rounded-xl border border-stone-300/70 bg-white shadow-sm lg:w-[420px] ${
            fullscreen
              ? ""
              : "lg:sticky lg:top-4 lg:self-start lg:max-h-[calc(100vh-6rem)]"
          }`}
          data-testid="screenplay-side-panel"
        >
          <div className="flex border-b border-stone-200">
            {sideTabs.map((t) => (
              <button
                key={t.id}
                onClick={() => setSidePanel(t.id)}
                data-testid={`sp-side-tab-${t.id}`}
                className={`flex-1 px-3 py-2 text-[11px] uppercase tracking-widest transition ${
                  sidePanel === t.id
                    ? "border-b-2 border-[#26775E] bg-[#26775E]/5 text-[#26775E]"
                    : "text-stone-500 hover:text-stone-800"
                }`}
              >
                {t.label}
                {t.count != null ? <span className="ml-1 text-stone-400">({t.count})</span> : null}
              </button>
            ))}
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {sidePanel === "characters" && <CharactersPanel {...charPanelProps} />}
            {sidePanel === "assistant" && (
              <AssistantPanel
                aiBusy={aiBusy}
                aiError={aiError}
                aiPreview={aiPreview}
                aiContext={aiContext}
                selectedCount={selectedIds.size}
                charNames={charNames}
                cursorContext={cursorContext}
                directions={directions}
                initialMode={assistantInitialMode}
                initialModeNonce={assistantModeNonce}
                onGenerateSequence={aiGenerateSequence}
                onGenerateDialogue={aiGenerateDialogue}
                onRewriteSelection={aiRewriteSelection}
                onSuggestDirections={aiSuggestDirections}
                onApplyPreview={applyAiPreview}
                onDismissPreview={() => setAiPreview(null)}
                onDismissDirections={() => setDirections(null)}
                onRetry={retryLastAiAction}
                canRetry={!!lastAiActionRef.current}
                aiElapsed={aiElapsed}
                onCancel={cancelAi}
              />
            )}
            {sidePanel === "scenes" && (
              <ScenesPanel scenes={scenesIndex} onJump={(elementId) => setAutoFocusId(elementId)} />
            )}
            {sidePanel === "notes" && (
              <NotesPanel value={screenplay.notes || ""} onChange={(v) => updateScreenplay({ notes: v })} />
            )}
          </div>
        </div>
      </div>

      {fullscreen && <EscListener onEscape={() => setFullscreen(false)} />}

      {/* Modales de la barra de Herramientas (🔤 Ortografía + 🔄 Renombrar) */}
      <SpellcheckModal
        open={spellcheckOpen}
        onClose={() => setSpellcheckOpen(false)}
        projectId={projectId}
        screenplay={screenplay}
        onApplied={(updated) => { setScreenplay(updated); }}
      />
      <RenameCharacterModal
        open={renameOpen}
        onClose={() => setRenameOpen(false)}
        projectId={projectId}
        characters={screenplay?.characters || []}
        onApplied={(r) => { if (r?.screenplay) setScreenplay(r.screenplay); }}
      />

      {/* Toast flotante de feedback para copiar/cortar/pegar bloques.
          Aparece esquina inferior-derecha 2.2s y se autodescarta. Solo
          visible cuando hay feedback activo. */}
      {clipboardFeedback && (
        <div
          className="pointer-events-none fixed bottom-6 right-6 z-[60] rounded-full bg-[#2C2B29] px-4 py-2 font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-white shadow-lg"
          data-testid="sp-clipboard-toast"
          role="status"
        >
          {clipboardFeedback}
        </div>
      )}
    </Wrapper>
  );
}
